/**
 * Redirects an authenticated Sandbox user to any StudentPortal route,
 * transferring auth tokens via a short-lived first-party cookie scoped
 * to the parent domain shared by both apps (e.g. ".codeyoung.com").
 *
 * Why a cookie (not URL hash):
 *   - Tokens never appear in the URL, browser history, Sentry breadcrumbs,
 *     extension URL listeners, or shared screenshots.
 *   - Cookie is origin-scoped to *.codeyoung.com so other sites can't read it.
 *   - 30-second Max-Age plus immediate delete-on-consume limits replay window.
 *
 * Fallback: when origins don't share a usable cookie domain (e.g. a developer
 * runs both apps on disparate localhost hostnames), the token is passed in the
 * URL hash and the receiver cleans it on first paint.
 *
 * Public API:
 *   redirectToStudentPortal("community")
 *   redirectToStudentPortal("buy-credits", { newTab: true })
 *   redirectToStudentPortal("community", { query: { tab: "projects" } })
 */

import Axios from "axios";
import jwtDecode from "jwt-decode";
import { decrypt, encrypt } from "./others";
import { setCookie, computeSharedCookieDomain } from "./cookieUtils";

const BRIDGE_COOKIE_NAME = "_cy_auth_bridge";
const BRIDGE_TTL_SECONDS = 30;
// In local dev both apps share the same hostname (localhost) so we can't use
// the shared-domain cookie with the production URL. Override via env var or
// fall back to the conventional SP dev port.
const SP_LOCAL_URL =
  process.env.REACT_APP_SP_LOCAL_URL || "http://localhost:3002";

/**
 * StudentPortal frontend origin. DERIVED FROM THE CURRENT HOST so it always
 * targets the same environment the user is in — never a wrong-env jump (the old
 * `REACT_APP_SP_FRONTEND_URL || prod` fallback sent stage users to prod):
 *
 *   sandbox.dev.codeyoung.com → https://studentportal.dev.codeyoung.com
 *   sandbox.codeyoung.com      → https://studentportal.codeyoung.com
 *   localhost                  → REACT_APP_SP_LOCAL_URL or :3002 (cross-port)
 */
const getSpFrontendUrl = () => {
  const host = window.location.hostname;
  if (host === "localhost") return SP_LOCAL_URL;
  const parent = host.split(".").slice(1).join(".");
  return parent
    ? `https://studentportal.${parent}`
    : "https://studentportal.codeyoung.com";
};

const CY_SSO_BASE_URL =
  process.env.REACT_APP_SSO_BASE_URL || "https://cy-sso.codeyoung.com/";

/**
 * Ensure the sandbox token in `localStorage.sandbox_user` is already a
 * cy-sso-issued token before we hand it to StudentPortal.
 *
 * Why this matters:
 *   The SP frontend's request interceptor will run its own one-shot
 *   migration on first API call by hitting `cy-sso /api/login/student/sp-auth`
 *   (which expects an SP-issued token). If we bridge a *legacy sandbox*
 *   token, SP's migration calls the wrong exchange route, fails, and every
 *   subsequent SP API call returns 401 "Invalid Authorization".
 *
 * Fix: run sandbox-side migration here (the correct `/student/sandbox-auth`
 * route) so the token we ship is guaranteed to be cy-sso. Already-migrated
 * tokens (decoded JWT has `accessTokenSecretId`) are short-circuited.
 *
 * Non-student roles (mentor/guest/admin) keep legacy tokens — bridge is
 * student-only anyway, but we still skip migration safely for them.
 */
const ensureSandboxTokenMigrated = async (sandboxUser) => {
  if (!sandboxUser?.ACCESS_TOKEN || !sandboxUser?.REFRESH_TOKEN)
    return sandboxUser;
  if (sandboxUser.role && sandboxUser.role !== "student") return sandboxUser;

  let decoded;
  try {
    decoded = jwtDecode(sandboxUser.ACCESS_TOKEN);
  } catch {
    return sandboxUser;
  }
  if (decoded?.accessTokenSecretId) return sandboxUser; // already cy-sso

  try {
    const { data } = await Axios.post(
      `${CY_SSO_BASE_URL}api/login/student/sandbox-auth`,
      {
        sandboxAccessToken: sandboxUser.ACCESS_TOKEN,
        sandboxRefreshToken: sandboxUser.REFRESH_TOKEN,
      },
      { headers: { "auth-source": "cy-sso" } },
    );
    if (!data?.valid || !data?.tokens) return sandboxUser;

    const upgraded = {
      ...sandboxUser,
      ACCESS_TOKEN: data.tokens.accessToken,
      REFRESH_TOKEN: data.tokens.refreshToken,
      Authorization: data.tokens.accessToken,
      "refresh-token": data.tokens.refreshToken,
    };
    localStorage.setItem("sandbox_user", encrypt(upgraded));
    window.dispatchEvent(new Event("sandbox_user_update"));
    return upgraded;
  } catch (e) {
    console.warn("[AuthBridge] Sandbox→cy-sso migration failed:", e);
    return sandboxUser; // bridge the legacy token anyway; SP will surface 401
  }
};

/**
 * Build the SP-shaped user object that mirrors the StudentPortal
 * `/login/student` API response (which SP stores verbatim as `localStorage.user`).
 *
 * Field names + casing match SP's login flow exactly so any SP code reading
 * `local.studentName`, `local.studentEmail`, `local.profilePicUrl`, `local.source`,
 * `local.ACCESS_TOKEN`, `local.REFRESH_TOKEN` works without changes.
 *
 * `userName` exists in sandbox but SP calls it `studentName` — mapped here.
 * Sandbox-specific fields (Authorization, refresh-token, role, user_id, isPaid)
 * are passed through too so any future SP consumer can read them.
 */
const buildSpUserObject = (sandboxUser) => {
  const studentName = sandboxUser.studentName || sandboxUser.userName || "";

  return {
    // Tokens — exact same keys SP expects
    ACCESS_TOKEN: sandboxUser.ACCESS_TOKEN,
    REFRESH_TOKEN: sandboxUser.REFRESH_TOKEN,

    // SP login-response fields (same names, same casing as SP /login/student)
    msg: "Log-in successful",
    studentName,
    studentId: sandboxUser.userId || sandboxUser.user_id || "",
    studentEmail: sandboxUser.email || "",
    profilePicUrl: sandboxUser.profilePicUrl || "",
    source: sandboxUser.source || "Codeyoung",

    // Sandbox extras — passed through verbatim, no renaming
    Authorization: sandboxUser.Authorization || sandboxUser.ACCESS_TOKEN,
    "refresh-token": sandboxUser["refresh-token"] || sandboxUser.REFRESH_TOKEN,
    role: sandboxUser.role || "student",
    user_id: sandboxUser.user_id || "",
    isPaid: sandboxUser.isPaid ?? false,
  };
};

// `from` tells StudentPortal the student came from standalone Sandbox, so its exits
// ("Go back", success screens) return here instead of to the portal's dashboard.
const buildBridgePayload = (sandboxUser) => ({
  user: buildSpUserObject(sandboxUser),
  ts: Date.now(),
  nonce: Math.random().toString(36).slice(2, 10),
  from: "sandbox",
});

const buildTargetUrl = (route, query) => {
  const cleanRoute = String(route || "dashboard").replace(/^\/+/, "");
  let url = `${getSpFrontendUrl()}/#/${cleanRoute}`;
  if (query && typeof query === "object") {
    const qs = Object.entries(query)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    if (qs) url += (cleanRoute.includes("?") ? "&" : "?") + qs;
  }
  return url;
};

const navigate = (url, newTab) => {
  if (newTab) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.href = url;
  }
};

/**
 * @param {string} route             SP route after "/#/" — e.g. "community"
 * @param {object} [opts]
 * @param {boolean} [opts.newTab]    Open in a new tab
 * @param {object} [opts.query]      Extra query params merged into target URL
 */
export const redirectToStudentPortal = async (
  route = "dashboard",
  opts = {},
) => {
  const { newTab = false, query } = opts;

  // 1. Auth check — must have both tokens to bridge
  let userData;
  try {
    userData = decrypt(localStorage.getItem("sandbox_user"));
  } catch {
    userData = null;
  }
  if (!userData?.ACCESS_TOKEN || !userData?.REFRESH_TOKEN) {
    navigate(`${getSpFrontendUrl()}/#/`, newTab);
    return;
  }

  // 2. MFE mode — Sandbox is embedded inside SP. Use parent's router instead
  //    of a cross-domain navigation that would tear down the parent app.
  if (typeof window !== "undefined" && window.__SANDBOX_IS_PLUGIN__) {
    const cleanRoute = String(route || "dashboard").replace(/^\/+/, "");
    window.dispatchEvent(
      new CustomEvent("cy:navigate-sp", {
        detail: { route: cleanRoute, query },
      }),
    );
    return;
  }

  // 3. Ensure we ship a cy-sso token (not a legacy sandbox JWT). SP's first
  //    API call would otherwise route this through the wrong migration path.
  userData = await ensureSandboxTokenMigrated(userData);

  const targetUrl = buildTargetUrl(route, query);
  const payload = buildBridgePayload(userData);

  // 3. Try cookie transport — preferred path
  const sharedDomain = computeSharedCookieDomain(
    window.location.hostname,
    new URL(getSpFrontendUrl()).hostname,
  );

  if (sharedDomain) {
    try {
      setCookie(BRIDGE_COOKIE_NAME, JSON.stringify(payload), {
        domain: sharedDomain === "localhost" ? undefined : sharedDomain,
        path: "/",
        maxAge: BRIDGE_TTL_SECONDS,
        sameSite: "Lax",
        secure: true,
      });
      navigate(targetUrl, newTab);
      return;
    } catch (e) {
      // fall through to URL-hash fallback
      console.warn("[AuthBridge] Cookie set failed, falling back to URL:", e);
    }
  }

  // 4. URL-hash fallback (localhost cross-port dev). Tokens go in the hash —
  //    never sent to server, but visible in history. Acceptable for dev only.
  const safeToken = encodeURIComponent(encrypt(payload));
  const separator = targetUrl.includes("?") ? "&" : "?";
  navigate(`${targetUrl}${separator}_spb=${safeToken}`, newTab);
};

export default redirectToStudentPortal;
